import { createFileRoute } from '@tanstack/react-router';
import { Sidebar } from '@/components/layout/sidebar';
import { useMyCommission, useMyRecordings, useTodayDialStats } from '@/features/my-dashboard/queries';
import { formatMoney } from '@/lib/format';

export const Route = createFileRoute('/_auth/my-dashboard')({ component: MyDashboardPage });

function MyDashboardPage(): JSX.Element {
  const { data: dials } = useTodayDialStats();
  const { data: comm } = useMyCommission();
  const { data: recs } = useMyRecordings(10);

  const total = dials?.dials_total ?? 0;
  const connected = dials?.dials_connected ?? 0;
  const conversed = dials?.dials_conversed ?? 0;
  const demos = dials?.demos_booked ?? 0;
  const target = 100; // FUTURE: pull from profile.daily_dial_target

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="pl-14 pr-6 md:px-6 py-4 border-b border-border/[0.08]">
          <h1 className="font-display text-2xl">My Dashboard</h1>
          <p className="text-text-dim text-sm mt-1">Your stats, commission, and recordings.</p>
        </div>

        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile label="Dials Today" value={`${total} / ${target}`} progress={total / target} />
          <StatTile label="Connect Rate" value={`${total > 0 ? Math.round((connected / total) * 100) : 0}%`} />
          <StatTile label="Conversation Rate" value={`${total > 0 ? Math.round((conversed / total) * 100) : 0}%`} />
          <StatTile label="Demos Booked" value={`${demos}`} />
        </div>

        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatTile label="Commission Earned" value={formatMoney(comm?.total_earned ?? 0)} />
          <StatTile label="Pending" value={formatMoney(comm?.total_pending ?? 0)} />
          <StatTile label="Paid" value={formatMoney(comm?.total_paid ?? 0)} />
        </div>

        <div className="px-6 py-5">
          <h2 className="text-sm font-semibold mb-3">Recent recordings</h2>
          {(recs ?? []).length === 0 ? (
            <p className="text-text-mute text-sm">No recordings yet.</p>
          ) : (
            <div className="space-y-2">
              {(recs ?? []).map((r) => {
                const when = r.started_at ? new Date(r.started_at).toLocaleString('en-GB') : '—';
                const dur = r.duration_seconds
                  ? `${Math.floor(r.duration_seconds / 60)}m ${r.duration_seconds % 60}s`
                  : '';
                return (
                  <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded bg-surface-2 border border-border/[0.08]">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        {r.to_number ?? 'Unknown'} <span className="text-accent">· {r.disposition ?? '—'}</span>
                      </div>
                      <div className="text-xs text-text-mute">{when} {dur && `· ${dur}`}</div>
                    </div>
                    {r.recording_url && (
                      <audio controls preload="none" src={r.recording_url} className="h-8" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatTile({ label, value, progress }: { label: string; value: string; progress?: number }): JSX.Element {
  return (
    <div className="bg-surface-2 border border-border/[0.08] rounded p-4">
      <div className="text-[10px] font-medium uppercase tracking-wider text-text-mute">{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
      {progress !== undefined && (
        <div className="mt-3 h-1 bg-bg rounded overflow-hidden">
          <div className="h-full bg-accent" style={{ width: `${Math.min(100, progress * 100)}%` }} />
        </div>
      )}
    </div>
  );
}
